import type { LabelDescriptionRegion } from "@repo/schema";
import type { Annotation } from "../types/annotations";

/** Shoelace area, in the same percent² units as a bbox's width × height. */
const polygonArea = (points: ReadonlyArray<[number, number]>): number =>
  Math.abs(
    points.reduce((sum, [x, y], i) => {
      const [nx, ny] = points[(i + 1) % points.length]!;
      return sum + x * ny - nx * y;
    }, 0),
  ) / 2;

const toRegion = (a: Annotation): LabelDescriptionRegion | null => {
  if (a.type === "bbox" && a.bbox && a.bbox.width > 0 && a.bbox.height > 0) {
    return { type: "bbox", ...a.bbox };
  }
  if (a.type === "polygon" && a.points && a.points.length >= 3) {
    return { type: "polygon", points: a.points };
  }
  return null;
};

const area = (region: LabelDescriptionRegion): number =>
  region.type === "bbox" ? region.width * region.height : polygonArea(region.points);

/**
 * The region of `labelId` on the open image to show Robopipe Vision when it
 * writes that label's description: the selected one if it has the label,
 * otherwise the largest (the most pixels to describe). Saved or not: the
 * geometry travels in the request. Undefined when the image has none, and the
 * server falls back to a saved region elsewhere.
 */
export const pickExampleRegion = (
  annotations: ReadonlyArray<Annotation>,
  labelId: string,
  selectedId: string | null,
): LabelDescriptionRegion | undefined => {
  const selected = annotations.find(
    (a) => a.id === selectedId && a.labelId === labelId && !a.inferred,
  );
  const selectedRegion = selected ? toRegion(selected) : null;
  if (selectedRegion) return selectedRegion;

  let best: LabelDescriptionRegion | undefined;
  for (const annotation of annotations) {
    if (annotation.labelId !== labelId || annotation.inferred) continue;
    const region = toRegion(annotation);
    if (region && (!best || area(region) > area(best))) best = region;
  }
  return best;
};
